/**
 * Trading widget registrations — side-effect module.
 *
 * Importing this module registers every Trading Dashboard widget on the
 * trading-only registry instance. Imported once from App.tsx.
 */

import { tradingWidgetRegistry } from './registry';
import { OptionMonitorWidget } from '../../features/widgets/option-monitor/OptionMonitorWidget';
import { StockMonitorWidget } from '../../features/widgets/stock-monitor/StockMonitorWidget';
import { OrderBlotterWidget } from '../../features/widgets/order-blotter/OrderBlotterWidget';
import { PortfolioWidget } from '../../features/widgets/portfolio/PortfolioWidget';
import { PositionsWidget } from '../../features/widgets/positions/PositionsWidget';
import { StrategyPositionsWidget } from '../../features/widgets/strategy-positions/StrategyPositionsWidget';
import { BacktestsWidget } from '../../features/widgets/backtests/BacktestsWidget';
import { HistoricalPnlsWidget } from '../../features/widgets/historical-pnls/HistoricalPnlsWidget';

export { tradingWidgetRegistry } from './registry';
export type { LayoutTemplate } from './templates';
export { LAYOUT_TEMPLATES } from './templates';

tradingWidgetRegistry.register({
  widgetType: 'option-monitor',
  displayName: 'Option Monitor',
  description: 'Options chain explorer with expiration/strike filtering and live tick display',
  icon: 'table-properties',
  component: OptionMonitorWidget,
  defaultSize: { width: 6, height: 12 },
  minSize: { width: 4, height: 4 },
});

tradingWidgetRegistry.register({
  widgetType: 'stock-monitor',
  displayName: 'Stock Monitor',
  description: 'Live stock tick display with symbol subscribe/unsubscribe',
  icon: 'activity',
  component: StockMonitorWidget,
  defaultSize: { width: 6, height: 6 },
  minSize: { width: 3, height: 3 },
});

tradingWidgetRegistry.register({
  widgetType: 'order-blotter',
  displayName: 'Order Blotter',
  description: 'Order list with cancel actions, strategy close, and real-time updates',
  icon: 'list-checks',
  component: OrderBlotterWidget,
  defaultSize: { width: 6, height: 6 },
  minSize: { width: 4, height: 3 },
});

tradingWidgetRegistry.register({
  widgetType: 'portfolio',
  displayName: 'Portfolio',
  description: 'Backtests, positions, and strategy positions with live P&L',
  icon: 'briefcase-business',
  component: PortfolioWidget,
  defaultSize: { width: 12, height: 20 },
  minSize: { width: 6, height: 5 },
});

tradingWidgetRegistry.register({
  widgetType: 'positions',
  displayName: 'Positions',
  description: 'Open positions with live mark price and P&L',
  icon: 'layers',
  component: PositionsWidget,
  defaultSize: { width: 12, height: 10 },
  minSize: { width: 6, height: 3 },
});

tradingWidgetRegistry.register({
  widgetType: 'strategy-positions',
  displayName: 'Strategy Positions',
  description: 'Multi-leg strategy positions with expandable details',
  icon: 'git-branch',
  component: StrategyPositionsWidget,
  defaultSize: { width: 6, height: 10 },
  minSize: { width: 6, height: 3 },
});

tradingWidgetRegistry.register({
  widgetType: 'historical-pnls',
  displayName: 'Historical Pnls',
  description: 'Realized P&L history from closed quantities with strategy drilldown',
  icon: 'history',
  component: HistoricalPnlsWidget,
  defaultSize: { width: 6, height: 10 },
  minSize: { width: 6, height: 4 },
  hideShellTitleBar: true,
});

tradingWidgetRegistry.register({
  widgetType: 'backtests',
  displayName: 'Backtests',
  description: 'Saved backtest results with statistics and charts',
  icon: 'flask-conical',
  component: BacktestsWidget,
  defaultSize: { width: 6, height: 10 },
  minSize: { width: 6, height: 4 },
});
